import { useEffect, useState } from "react";

import { fetchCollections, fetchFolder, saveFolder } from "./api";
import { navigate } from "./navigation";
import type { CollectionSummary, SchoolSubject } from "./types";

export function FolderEditor({ subjects, folderId, canOfficial }: { subjects: SchoolSubject[]; folderId?: number; canOfficial: boolean }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [subjectId, setSubjectId] = useState(subjects[0]?.id ?? 0);
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isOfficial, setIsOfficial] = useState(false);
  const [loading, setLoading] = useState(Boolean(folderId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void fetchCollections(false, 1, 100)
      .then((payload) => { if (active) setCollections(payload.collections); })
      .catch((reason: unknown) => { if (active) setError(reason instanceof Error ? reason.message : "Séries indisponibles."); });
    if (folderId) {
      void fetchFolder(folderId)
        .then((folder) => {
          if (!active) return;
          if (!folder.can_edit) throw new Error("Cette chaîne ne peut pas être modifiée.");
          setTitle(folder.title);
          setDescription(folder.description ?? "");
          setSubjectId(folder.subject.id);
          setSelectedIds(folder.collections.map((item) => item.id));
          setIsOfficial(folder.is_official);
        })
        .catch((reason: unknown) => { if (active) setError(reason instanceof Error ? reason.message : "Chaîne indisponible."); })
        .finally(() => { if (active) setLoading(false); });
    }
    return () => { active = false; };
  }, [folderId]);

  const toggleCollection = (collectionId: number) => {
    setSelectedIds((current) => current.includes(collectionId) ? current.filter((id) => id !== collectionId) : [...current, collectionId]);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving || !title.trim() || !subjectId) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveFolder({ title: title.trim(), description: description.trim(), subject_id: subjectId, collection_ids: selectedIds, is_official: canOfficial && isOfficial }, folderId);
      navigate(result.path);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "La chaîne n’a pas pu être enregistrée.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="collection-loading">Chargement du formulaire…</div>;
  return (
    <section className="collection-page folder-editor-page">
      <button className="back-button" type="button" onClick={() => navigate(folderId ? `/chaines/${folderId}` : "/mes-chaines")}>← Annuler et revenir aux chaînes</button>
      <div className="collection-heading"><div><p className="eyebrow">Édition pédagogique</p><h1>{folderId ? "Modifier la chaîne" : "Créer une chaîne"}</h1><p>Composez un parcours autour de séries accessibles à vos élèves.</p></div></div>
      {error && <div className="info-notice" role="alert">{error}</div>}
      <form className="folder-editor-form" onSubmit={(event) => void submit(event)}>
        <div className="folder-fields">
          <label>Titre<span>{title.length}/150</span><input required maxLength={150} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ex. Réviser les fondamentaux" /></label>
          <label>Matière<select required value={subjectId || ""} onChange={(event) => setSubjectId(Number(event.target.value))}><option value="" disabled>Choisir une matière</option>{subjects.map((subject) => <option key={subject.id} value={subject.id}>{subject.name}</option>)}</select></label>
          <label className="wide">Description<span>{description.length}/250</span><textarea maxLength={250} rows={4} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Décrivez l’objectif pédagogique." /></label>
          {canOfficial && <label className="wide official-checkbox"><input type="checkbox" checked={isOfficial} onChange={(event) => setIsOfficial(event.target.checked)} /><span><strong>Ressource officielle</strong><small>La chaîne sera publiée et visible dans les ressources officielles.</small></span></label>}
        </div>
        <fieldset className="folder-series-picker"><legend>Séries de la chaîne <span>{selectedIds.length} sélectionnée{selectedIds.length > 1 ? "s" : ""}</span></legend>{collections.length ? <div className="folder-series-grid">{collections.map((collection) => <label key={collection.id} className={selectedIds.includes(collection.id) ? "selected" : ""}><input type="checkbox" checked={selectedIds.includes(collection.id)} onChange={() => toggleCollection(collection.id)} /><span className="subject-color" style={{ backgroundColor: collection.subject.color }} /><span><strong>{collection.title}</strong><small>{collection.subject.name} · {collection.cards_count} cartes</small></span></label>)}</div> : <p>Aucune série accessible. Créez ou publiez d’abord une série.</p>}</fieldset>
        <div className="folder-editor-actions"><button className="button" type="button" onClick={() => navigate("/mes-chaines")}>Annuler</button><button className="button button-primary" type="submit" disabled={saving || !title.trim() || !subjectId}>{saving ? "Enregistrement…" : folderId ? "Enregistrer les modifications" : "Créer la chaîne"}</button></div>
      </form>
    </section>
  );
}
